import { useEffect, useMemo, useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import EmailService from "@/app/_services/EmailService";
import { templateService } from "@/app/_services/TemplateService";
import { toast } from "@/app/_utils/toast";
import { emailSchema } from "./EmailSchema";

type FormData = yup.InferType<typeof emailSchema>;

export type EmailTemplate = {
  id: string;
  title: string;
  thumbnail?: string | null;
  is_sample?: boolean;
  template?: string;
  template_json?: unknown;
};

export function useEmailFormFlow({
  open,
  campaignId,
  onCreated,
}: {
  open: boolean;
  campaignId?: string;
  onCreated: (id: string) => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(emailSchema),
    defaultValues: { name: "", subject: "", template_id: null },
  });

  useEffect(() => {
    if (!open) return;
    setTemplatesLoading(true);
    Promise.all([
      templateService.getTemplatesAsync(true),
      templateService.getTemplatesAsync(false),
    ])
      .then(([starters, userTemplates]) =>
        setTemplates([...(starters ?? []), ...(userTemplates ?? [])])
      )
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [open]);

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.title?.toLowerCase().includes(q));
  }, [templates, templateSearch]);

  const selectedTemplateId = watch("template_id");

  function selectTemplate(id: string | null) {
    setValue("template_id", id, { shouldDirty: true });
  }

  function resetForm() {
    reset({ name: "", subject: "", template_id: null });
    setTemplateSearch("");
  }

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    try {
      const template = templates.find((t) => t.id === fields.template_id);
      const email = await EmailService.createEmailAsync({
        name: fields.name.trim(),
        subject: fields.subject.trim(),
        body: template?.template ?? "",
        body_json: template?.template_json ?? null,
        ...(campaignId ? { campaign: campaignId } : {}),
      });
      toast.success("Email created");
      resetForm();
      onCreated(email.id);
    } catch (e) {
      toast.error("Failed to create email");
    } finally {
      setIsSaving(false);
    }
  });

  return {
    control,
    errors,
    isSaving,
    handleSave,
    resetForm,
    templates: filteredTemplates,
    templatesLoading,
    templateSearch,
    setTemplateSearch,
    selectedTemplateId,
    selectTemplate,
  };
}
